import { useEffect, useMemo } from 'react'

import { useAuthStore } from '#/shared/lib/auth'
import { sanitizeSmsFragment } from '#/shared/lib/sms-sanitize'
import { localModelFor } from '#/shared/lib/smart-compose'
import type { CompletionSource } from '#/shared/ui/note-editor'

import type { TimelineItem } from './conversation'


type MessageItem = Extract<TimelineItem, { kind: 'message' }>

function messageItems(timeline: ReadonlyArray<TimelineItem>): MessageItem[] {
  return timeline.filter(
    (item): item is MessageItem =>
      item.kind === 'message' && item.text.trim() !== '',
  )
}

/** The agent's own words in this thread: outbound, human-written, actually
 *  sent — the material the local model learns from. */
function learnableMessages(timeline: ReadonlyArray<TimelineItem>) {
  return messageItems(timeline)
    .filter(
      (item) =>
        item.direction === 'out' &&
        item.ai !== true &&
        item.status !== 'failed' &&
        item.status !== 'sending',
    )
    .map((item) => ({ at: item.at, text: item.text }))
}

/**
 * Builds the composer's autocomplete source: the per-agent local model, learned
 * from their own sent messages and answering instantly from the device.
 *
 * The product pairs this with a Gemini call for pairs the agent has not typed
 * before. The console does not: that call goes from the browser straight to
 * Google with the key in a header, and a `VITE_` key is readable by anyone who
 * opens the app. The local half is free and needs no credential, so it is all
 * that ships here.
 *
 * The thread history also *teaches* the model, watermarked so a re-opened
 * thread never double-counts. Accepted suggestions are SMS-sanitized (fancy
 * Unicode trips the Telnyx spam block). Undefined — autocomplete off — when
 * there is no signed-in user to attribute the model to.
 */
export function useSmartCompose(
  timeline?: ReadonlyArray<TimelineItem>,
): CompletionSource | undefined {
  const userId = useAuthStore((state) => state.user?.id)
  const model = useMemo(
    () => (userId !== undefined ? localModelFor(userId) : undefined),
    [userId],
  )

  useEffect(() => {
    if (!model || !timeline) return
    model.learnMessages(learnableMessages(timeline))
  }, [model, timeline])

  return useMemo(() => {
    if (!model) return undefined
    return ({ text }) => {
      const local = model.predict(text)
      return Promise.resolve(local ? sanitizeSmsFragment(local) : '')
    }
  }, [model])
}
